import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthPageWrapper from '../../components/AuthWrapper/AuthPageWrapper';
import BannerPicker from '../../components/BannerPicker/BannerPicker';
import CategoryPicker from '../../components/CategoryPicker/CategoryPicker';
import Input from '../../components/Input/Input';
import { appContext } from '../../service/AppContext';
import './Broadcast.css';

export default function BroadcastNew() {
    const { breslevRequest } = useContext(appContext);
    const navigate = useNavigate();
    const [languages, setLanguages] = useState([]);
    const [languageCode, setLanguageCode] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [categoryIds, setCategoryIds] = useState([]);
    const [bannerPostId, setBannerPostId] = useState(null);
    const [thumbnail, setThumbnail] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const languageFieldRef = useRef(null);

    const clearError = (field) => setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));

    useEffect(() => {
        (async () => {
            const res = await breslevRequest({ method: 'GET', route: '/broadcast/languages' });
            if (res?.status === 200 && Array.isArray(res.data)) {
                setLanguages(res.data);
                if (res.data[0]?.code) setLanguageCode(res.data[0].code);
            } else {
                toast('שגיאה בטעינת שפות');
            }
        })();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        const nextErrors = {};
        if (!languageCode) nextErrors.language = 'יש לבחור שפת שידור';
        if (!title.trim()) nextErrors.title = 'יש להזין כותרת';
        if (!description.trim()) nextErrors.description = 'יש להזין תיאור';
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            // Focus / scroll to the first invalid field, in form order.
            if (nextErrors.language) {
                languageFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (nextErrors.title) {
                document.getElementById('bf-title')?.focus();
            } else {
                document.getElementById('bf-description')?.focus();
            }
            return;
        }
        setErrors({});
        const form = new FormData();
        form.append('language_code', languageCode);
        form.append('title', title.trim());
        form.append('description', description);
        form.append('category_ids', JSON.stringify(categoryIds || []));
        if (bannerPostId) form.append('banner_post_id', bannerPostId);
        if (thumbnail) form.append('thumbnail', thumbnail);

        setSubmitting(true);
        const res = await breslevRequest({
            method: 'POST',
            route: '/broadcast/start',
            body: form,
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSubmitting(false);

        if (res?.status === 201 && res.data?.id) {
            toast('השידור התחיל. ניתן להתחיל לשדר ב-OBS');
            navigate(`/broadcast/${res.data.id}`);
        } else if (res?.status === 409) {
            toast('כבר קיים שידור פעיל בשפה זו');
        } else {
            toast('שגיאה ביצירת שידור');
        }
    };

    return (
        <AuthPageWrapper roles={['ContentManager', 'Admin']}>
            <div className='video-page-wrepper'>
                <div className='video-page'>
                    <div className='page-header'>התחלת שידור חי</div>
                    <hr />
                    <form className='page-container broadcast-form' onSubmit={onSubmit}>
                        <div className='bf-field' ref={languageFieldRef}>
                            <label>שפת השידור</label>
                            <div className='bf-radio-row'>
                                {languages.map(l => (
                                    <label key={l.code} className={'bf-radio' + (languageCode === l.code ? ' bf-radio-on' : '')}>
                                        <input
                                            type='radio'
                                            name='language'
                                            value={l.code}
                                            checked={languageCode === l.code}
                                            onChange={() => { setLanguageCode(l.code); clearError('language'); }}
                                        />
                                        <span>{l.name_he}</span>
                                        <small dir='ltr'>{l.stream_key}</small>
                                    </label>
                                ))}
                            </div>
                            {errors.language && <small className='bf-error-msg'>{errors.language}</small>}
                        </div>

                        <div className='bf-field'>
                            <label>כותרת</label>
                            <Input
                                id='bf-title'
                                name='title'
                                className={'text-small text-black input-field' + (errors.title ? ' bf-invalid' : '')}
                                value={title}
                                onChange={e => { setTitle(e.target.value); clearError('title'); }}
                                placeholder='כותרת השידור'
                            />
                            {errors.title && <small className='bf-error-msg'>{errors.title}</small>}
                        </div>

                        <div className='bf-field'>
                            <label>תיאור</label>
                            <textarea
                                id='bf-description'
                                name='description'
                                className={errors.description ? 'bf-invalid' : ''}
                                rows={3}
                                placeholder='תיאור השידור'
                                value={description}
                                onChange={e => { setDescription(e.target.value); clearError('description'); }}
                            />
                            {errors.description && <small className='bf-error-msg'>{errors.description}</small>}
                        </div>

                        <div className='bf-field'>
                            <label>קטגוריות</label>
                            <CategoryPicker value={categoryIds} onChange={setCategoryIds} languageCode={languageCode} />
                        </div>

                        <div className='bf-field'>
                            <label>באנר — אופציונלי</label>
                            <BannerPicker value={bannerPostId} onChange={setBannerPostId} languageCode={languageCode} />
                        </div>

                        <div className='bf-field'>
                            <label>תמונה (Thumbnail) — אופציונלי</label>
                            <input
                                type='file'
                                accept='image/*'
                                onChange={e => setThumbnail(e.target.files?.[0] || null)}
                            />
                            <small>אם לא תועלה תמונה, תופק תמונה אוטומטית מאמצע הסרטון.</small>
                        </div>

                        <div className='bf-actions'>
                            <button type='submit' className='upload-button' disabled={submitting}>
                                {submitting ? 'יוצר שידור...' : 'התחל שידור'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </AuthPageWrapper>
    );
}
